/**
 * Shared booklet renderer – the single source of truth for the printed guide,
 * the workbook and the workbook's answer key.
 *
 * Loaded in the browser as window.BookletRender (plain <script>) and
 * require()'d by utils/pdf.js in Node. UMD, no DOM use: pure string building,
 * exactly like worksheet-render.js, and for the same reason — screen and print
 * must come from one renderer or they drift.
 *
 * Two documents come out of one booklet. The guide renders each section's
 * `blocks`; the workbook renders the same sections' `exercises` and collects
 * every answer into a key at the back. They are numbered from the same
 * section list, so Workbook 3.2 always practises Guide 3.2.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.BookletRender = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const Schema = (typeof module === 'object' && module.exports)
        ? require('./booklet-schema.js')
        : (typeof window !== 'undefined' ? window.BookletSchema : null);

    const CurveDiagram = (typeof module === 'object' && module.exports)
        ? require('./curve-diagram.js')
        : (typeof window !== 'undefined' ? window.CurveDiagram : null);

    const BrandMark = (typeof module === 'object' && module.exports)
        ? require('./brand-mark.js')
        : (typeof window !== 'undefined' ? window.BrandMark : null);

    // Same environment split again. A booklet has no per-task instructions to
    // translate, so what a home language buys here is navigation and
    // vocabulary: the page headings get a translated twin and the glossary
    // grows a column. See public/js/home-languages.js.
    const HomeLanguages = (typeof module === 'object' && module.exports)
        ? require('./home-languages.js')
        : (typeof window !== 'undefined' ? window.HomeLanguages : null);

    function esc(str) {
        return String(str == null ? '' : str)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function escAttr(str) {
        return esc(str).replace(/"/g, '&quot;');
    }

    /**
     * A heading's translated twin, inline after the English.
     *
     * Inline rather than on its own line because these are one or two words on
     * a page heading; a second line for "Answers" would cost more space than
     * the word is worth. Empty string when the booklet is English-only, so
     * every call site can concatenate unconditionally.
     */
    function l1(language, key) {
        const lang = HomeLanguages && HomeLanguages.getLanguage(language);
        const text = lang && HomeLanguages.uiString(language, key);
        if (!text) return '';
        return ` <span class="bk-l1" lang="${lang.code}" dir="${lang.dir}">${esc(text)}</span>`;
    }

    // Same environment split as the module wrapper: Node gets the katex npm
    // package, the browser the already-loaded global. Mirrors mathText in
    // worksheet-render.js, which is not exported to be shared.
    // The browser's is looked up each time, for the reason given there: the
    // main app fetches katex after this file has run.
    const nodeKatex = (typeof module === 'object' && module.exports) ? require('katex') : null;
    const katexLib = () => nodeKatex || (typeof window !== 'undefined' && window.katex) || null;

    const MATH_REGEX = /\$\$([^$]+?)\$\$|\$([^$\n]+?)\$/g;

    /** esc() plus $…$ / $$…$$ LaTeX. Falls back to plain esc() without katex. */
    function mathText(str) {
        const s = String(str == null ? '' : str);
        const katex = katexLib();
        if (!katex || !s.includes('$')) return esc(s);
        let out = '';
        let last = 0;
        let m;
        MATH_REGEX.lastIndex = 0;
        while ((m = MATH_REGEX.exec(s)) !== null) {
            out += esc(s.slice(last, m.index));
            const isBlock = m[1] !== undefined;
            const tex = isBlock ? m[1] : m[2];
            try {
                // A _____ gap inside a formula is drawn as an empty answer box (see worksheet-render.js).
                out += katex.renderToString(tex.replace(/(?:\\?_){3,}/g, '{\\boxed{\\phantom{00}}}'), { throwOnError: false, displayMode: isBlock });
            } catch {
                out += esc(tex);
            }
            last = MATH_REGEX.lastIndex;
        }
        return out + esc(s.slice(last));
    }

    /**
     * mathText plus the [[key term]] highlighting a revision guide runs on.
     *
     * Applied to the already-escaped, already-KaTeX'd string rather than to the
     * raw one: esc() leaves square brackets alone, and rendered KaTeX is HTML
     * that never contains them, so the marker survives both passes intact and
     * cannot be forged by a model emitting literal angle brackets.
     */
    function bodyText(str) {
        var re = Schema ? Schema.KEY_TERM_RE : /\[\[([^\[\]]{1,80})\]\]/g;
        re.lastIndex = 0;
        return mathText(str).replace(re, '<span class="bk-key">$1</span>');
    }

    /**
     * Question text that may carry a markdown pipe table inside it.
     *
     * The exercise schema has no table field, so when a question needs one the
     * model writes it into the question string as markdown. Run through
     * bodyText() alone that prints as a row of literal pipes, which is what a
     * pupil and a printed workbook both showed. Rather than widen the schema
     * (and reject every booklet already written against it), the pipes are
     * recognised on the way out and drawn as the table they describe.
     *
     * A table is a run of two or more lines that both start and end with a pipe.
     * The conventional |---|---| separator row is dropped if present, and the
     * first row becomes the header — the same shape the `table` block renders,
     * so a table inside a question looks like every other table in the booklet.
     * Anything that is not such a run is passed straight to bodyText().
     */
    function questionText(str) {
        var text = String(str == null ? '' : str);
        // The shared parser also reads the tables the model writes without
        // their outer pipes, and ignores pipes inside $maths$. The code below
        // is the fallback for a page that loaded the renderer without it.
        if (Schema && Schema.pipeTableSegments) {
            return Schema.pipeTableSegments(text).map(function (seg) {
                if (seg.type === 'text') return bodyText(seg.text);
                var head = seg.rows[0].map(function (c) { return '<th>' + bodyText(c) + '</th>'; }).join('');
                var body = seg.rows.slice(1).map(function (r) {
                    return '<tr>' + r.map(function (c) { return '<td>' + bodyText(c) + '</td>'; }).join('') + '</tr>';
                }).join('');
                return '<div class="bk-table-wrap"><table class="bk-table">'
                    + '<thead><tr>' + head + '</tr></thead><tbody>' + body + '</tbody></table></div>';
            }).join('');
        }
        if (!/^\s*\|.*\|\s*$/m.test(text)) return bodyText(text);

        var lines = text.split(/\r?\n/);
        var out = '';
        var buffer = [];
        var rows = [];

        function flushText() {
            var chunk = buffer.join('\n').trim();
            buffer = [];
            if (chunk) out += bodyText(chunk);
        }

        function flushTable() {
            if (!rows.length) return;
            var cells = rows.map(function (row) {
                // Trailing and leading pipes make an empty cell at each end.
                return row.trim().replace(/^\||\|$/g, '').split('|').map(function (c) { return c.trim(); });
            }).filter(function (r) {
                // The |---|:--:|---| separator carries no content.
                return !r.every(function (c) { return /^:?-{2,}:?$/.test(c); });
            });
            rows = [];
            if (!cells.length) return;

            var head = cells[0].map(function (c) { return '<th>' + bodyText(c) + '</th>'; }).join('');
            var body = cells.slice(1).map(function (r) {
                return '<tr>' + r.map(function (c) { return '<td>' + bodyText(c) + '</td>'; }).join('') + '</tr>';
            }).join('');
            out += '<div class="bk-table-wrap"><table class="bk-table">'
                + '<thead><tr>' + head + '</tr></thead><tbody>' + body + '</tbody></table></div>';
        }

        for (var i = 0; i < lines.length; i++) {
            if (/^\s*\|.*\|\s*$/.test(lines[i])) {
                if (!rows.length) flushText();
                rows.push(lines[i]);
            } else {
                if (rows.length) flushTable();
                buffer.push(lines[i]);
            }
        }
        flushTable();
        flushText();
        return out;
    }

    /**
     * Section numbers, as "chapter.section within chapter".
     *
     * Chapters are runs of adjacent sections sharing a chapter name rather than
     * a separate structure: that is how the plan stores them, and it means a
     * teacher who reorders sections in the approval screen gets renumbering for
     * free instead of a chapter that has quietly split in two.
     */
    function numberSections(sections) {
        const out = [];
        let chapterNo = 0;
        let withinChapter = 0;
        let lastChapter = null;
        for (const section of sections || []) {
            const chapter = section.chapter || '';
            if (chapter !== lastChapter) {
                chapterNo++;
                withinChapter = 0;
                lastChapter = chapter;
            }
            withinChapter++;
            out.push({
                section,
                chapter,
                chapterNo,
                number: `${chapterNo}.${withinChapter}`,
                startsChapter: withinChapter === 1,
            });
        }
        return out;
    }

    /** Sections that actually have something to print. */
    function renderable(sections, key) {
        return (sections || []).filter((s) => Array.isArray(s[key]) && s[key].length);
    }

    // ── Guide blocks ─────────────────────────────────────────────────────

    function listItems(items) {
        return (items || []).map((i) => `<li>${bodyText(i)}</li>`).join('');
    }

    function renderDiagram(block, section) {
        // The model describes the diagram; the image pass finds one and stores
        // it on the section. Without an image the caption alone would be a
        // reference to a picture that is not there, so nothing is printed.
        const url = section && section.imageUrl;
        // ...unless the picture is still being chosen. Images are resolved for
        // the whole document in one call after the last section is written
        // (resolveBookletImages in routes/booklets.js), so a section can sit on
        // screen for a while knowing it wants a picture and not yet having one.
        // A placeholder of the right size is what stops the section reflowing
        // under the reader when its figure finally lands.
        //
        // Strictly opt-in via `imagePending`, which only the live generation
        // path sets. Every other caller — the PDF, the print page, the student
        // view, a reopened booklet — renders nothing for a missing image, and
        // must: a shimmering grey box is furniture, and furniture printed into
        // a booklet is a defect.
        if (!url) return section && section.imagePending ? pendingDiagram(block) : '';
        const credit = section.imageCredit
            ? `<span class="bk-credit">${esc(section.imageCredit)}</span>` : '';
        const caption = block.caption ? mathText(block.caption) : '';
        // The alt text is the query and not the caption. The caption is
        // already on the page as text, so repeating it in alt reads the same
        // sentence twice to a screen reader and describes the picture to
        // nobody; the query is the writer's own account of what the picture
        // has to show, which is what alt is for. It falls back to the caption
        // only when there is no query to use.
        const alt = block.query || block.caption || '';
        return `<figure class="bk-figure">`
            // Deliberately not loading="lazy": the same markup is what
            // utils/pdf.js hands Puppeteer, and a deferred image below the
            // fold is one that is simply absent from the printed page.
            + `<img src="${escAttr(url)}" alt="${escAttr(alt)}">`
            + (caption || credit ? `<figcaption>${caption}${credit}</figcaption>` : '')
            + `</figure>`;
    }

    /**
     * The hole a picture is about to fill.
     *
     * Sized in the same band as a real figure (see .bk-figure in app.css) and
     * carrying the caption, because the caption is already known — it came
     * from the writer, not from the search — so the only thing missing is the
     * picture itself. aria-busy tells a screen reader this is in progress
     * rather than an image that failed to load.
     */
    function pendingDiagram(block) {
        const caption = block.caption ? mathText(block.caption) : '';
        return `<figure class="bk-figure bk-figure-pending" aria-busy="true">`
            + `<div class="bk-figure-skeleton" role="img" `
            + `aria-label="Finding a picture${block.query ? ` of ${escAttr(block.query)}` : ''}"></div>`
            + (caption ? `<figcaption>${caption}</figcaption>` : '')
            + `</figure>`;
    }

    function renderBlock(block, section) {
        switch (block.type) {
            // The backbone. Numbered with a CSS counter rather than <ol>'s own
            // markers so the number can be styled as the coloured, bracketed
            // "1)" the format uses, and so it stays aligned when a point runs
            // to three lines.
            case 'points':
                return `<div class="bk-points">`
                    + (block.title ? `<h4 class="bk-points-title">${bodyText(block.title)}</h4>` : '')
                    + `<ol>${(block.items || []).map((i) => `<li>${bodyText(i)}</li>`).join('')}</ol>`
                    + `</div>`;

            case 'prose':
                return `<p class="bk-p">${bodyText(block.text)}</p>`;

            case 'example':
                return `<div class="bk-example"><span class="bk-example-tag">Example</span>`
                    + `<div class="bk-example-body">${bodyText(block.text)}</div></div>`;

            case 'roundup':
                return `<div class="bk-roundup">`
                    + `<h4 class="bk-roundup-quip">${bodyText(block.quip)}</h4>`
                    + `<p class="bk-roundup-summary">${bodyText(block.summary)}</p></div>`;

            case 'key_facts':
                return `<aside class="bk-facts">`
                    + `<h4>${block.title ? bodyText(block.title) : 'Remember'}</h4>`
                    + `<ul>${listItems(block.items)}</ul></aside>`;

            case 'worked_example':
                return `<div class="bk-worked">`
                    + `<h4>Worked example</h4>`
                    + `<p class="bk-worked-q">${bodyText(block.question)}</p>`
                    + `<ol class="bk-steps">${listItems(block.steps)}</ol>`
                    + `<p class="bk-worked-a"><span>Answer</span> ${bodyText(block.answer)}</p>`
                    + `</div>`;

            case 'definition':
                return `<p class="bk-def">`
                    + `<span class="bk-term">${mathText(Schema ? Schema.stripKeyTerms(block.term) : block.term)}</span>`
                    + `<span class="bk-def-text">${bodyText(block.definition)}</span></p>`;

            case 'exam_tip':
                return `<aside class="bk-aside bk-tip">`
                    + `<span class="bk-aside-label">Exam tip</span>${bodyText(block.text)}</aside>`;

            case 'common_mistake':
                return `<aside class="bk-aside bk-mistake">`
                    + `<span class="bk-aside-label">Watch out</span>${bodyText(block.text)}</aside>`;

            case 'table': {
                const head = (block.headers || []).map((h) => `<th>${mathText(h)}</th>`).join('');
                const body = (block.rows || [])
                    .map((r) => `<tr>${r.map((c) => `<td>${bodyText(c)}</td>`).join('')}</tr>`)
                    .join('');
                return `<div class="bk-table-wrap">`
                    + (block.title ? `<h4 class="bk-table-title">${mathText(block.title)}</h4>` : '')
                    + `<table class="bk-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`
                    + `</div>`;
            }

            case 'equation':
                return `<div class="bk-equation">`
                    + `<div class="bk-equation-tex">${mathText(block.latex)}</div>`
                    + `<p class="bk-equation-meaning">${bodyText(block.meaning)}</p></div>`;

            case 'diagram':
                return renderDiagram(block, section);

            // Drawn, not found: there is no image to wait for and no credit to
            // print, so this never has a pending or an empty state.
            case 'curve_diagram': {
                const svg = CurveDiagram ? CurveDiagram.render(block.diagram) : '';
                if (!svg) return '';
                return `<figure class="bk-figure bk-figure--drawn">${svg}`
                    + (block.caption ? `<figcaption>${mathText(block.caption)}</figcaption>` : '')
                    + `</figure>`;
            }

            case 'quick_check': {
                const qs = (block.questions || []).map((q, i) =>
                    `<li>${bodyText(q.question)}<span class="bk-qc-a">${bodyText(q.answer)}</span></li>`
                ).join('');
                return `<aside class="bk-check"><h4>Quick check</h4><ol>${qs}</ol></aside>`;
            }

            default:
                return '';
        }
    }

    function renderGuideSection(entry) {
        const { section, number, chapter, startsChapter } = entry;
        const blocks = (section.blocks || []).map((b) => renderBlock(b, section)).join('');
        const chapterHead = startsChapter && chapter
            ? `<h2 class="bk-chapter"><span>Chapter ${entry.chapterNo}</span>${esc(chapter)}</h2>`
            : '';
        const codes = (section.specCodes || []).length
            ? `<p class="bk-codes">Specification ${esc(section.specCodes.join(', '))}</p>` : '';
        return chapterHead
            + `<section class="bk-section">`
            + `<h3 class="bk-heading"><span class="bk-num">${esc(number)}</span>${esc(section.heading)}</h3>`
            + codes
            + blocks
            + `</section>`;
    }

    // ── Front matter ─────────────────────────────────────────────────────

    function subtitleFor(booklet) {
        return [booklet.examBoard, booklet.subject, booklet.level]
            .filter(Boolean).map(esc).join(' · ');
    }

    /**
     * @param {object} [mark] the organisation's name and crest, drawn at the
     *   top of the cover panel. See brand-mark.js and utils/resourceMark.js.
     */
    function coverPage(booklet, kind, mark) {
        const label = kind === 'workbook' ? 'Workbook' : 'Revision guide';
        // Above the kind label rather than below the title: this is the front
        // of a bound document, and a crest is the first thing on the front of
        // every booklet a school has ever handed out.
        const brand = BrandMark ? BrandMark.headerHtml(mark, 'bk') : '';
        return `<header class="bk-cover">`
            + brand
            + `<p class="bk-kind">${label}</p>`
            + `<h1 class="bk-title">${esc(booklet.title || 'Revision guide')}</h1>`
            + `<p class="bk-sub">${subtitleFor(booklet)}</p>`
            + `</header>`;
    }

    function contentsPage(numbered, language) {
        if (numbered.length < 2) return '';
        const rows = numbered.map((e) => {
            const chapter = e.startsChapter && e.chapter
                ? `<li class="bk-toc-chapter">${esc(e.chapter)}</li>` : '';
            return chapter
                + `<li class="bk-toc-row"><span class="bk-toc-num">${esc(e.number)}</span>`
                + `<span>${esc(e.section.heading)}</span></li>`;
        }).join('');
        return `<nav class="bk-toc"><h2>Contents${l1(language, 'contents')}</h2><ol>${rows}</ol></nav>`;
    }

    function glossaryPage(sections, language) {
        const entries = Schema ? Schema.collectGlossary(sections) : [];
        if (!entries.length) return '';
        const lang = HomeLanguages && HomeLanguages.getLanguage(language);
        // Only once at least one term came back with a translation: the model
        // is told to leave the field out rather than invent an equivalent, so
        // a very technical glossary with none is a normal outcome, and a
        // column of blanks would read as a bug.
        const bilingual = !!lang && entries.some((g) => g.translation);
        const rows = entries.map((g) =>
            `<div class="bk-gloss-row"><dt>${mathText(g.term)}</dt><dd>${mathText(g.definition)}</dd>`
            + (bilingual
                ? `<dd class="bk-gloss-l1" lang="${lang.code}" dir="${lang.dir}">${esc(g.translation || '')}</dd>`
                : '')
            + `</div>`
        ).join('');
        return `<section class="bk-glossary${bilingual ? ' bk-glossary-bilingual' : ''}">`
            + `<h2>Glossary${l1(language, 'glossary')}</h2><dl>${rows}</dl></section>`;
    }

    /** The guide: everything the pupil reads. */
    function buildBookletHtml(booklet, sections, opts) {
        opts = opts || {};
        const usable = renderable(sections, 'blocks');
        const numbered = numberSections(usable);
        // Off the booklet itself, not off opts: the home language is part of
        // what the booklet is, so every render of it — screen, PDF, months
        // later — agrees without the caller having to remember to say.
        const language = (HomeLanguages && HomeLanguages.cleanLanguage(booklet && booklet.home_language)) || '';
        return `<main class="bk-doc">`
            + coverPage(booklet, 'booklet', opts.mark)
            + contentsPage(numbered, language)
            + numbered.map(renderGuideSection).join('')
            + glossaryPage(usable, language)
            + (BrandMark ? BrandMark.creditHtml(opts.mark, 'bk') : '')
            + `</main>`;
    }

    // ── Workbook ─────────────────────────────────────────────────────────

    const TIER_LABELS = { warmup: 'Warm-up', practice: 'Practice', exam_style: 'Exam-style' };

    function marksTag(n) {
        return n ? `<span class="bk-marks">[${n} mark${n === 1 ? '' : 's'}]</span>` : '';
    }

    /**
     * Ruled space to write in, sized to what the question is worth.
     *
     * Marks are the only signal available for how much a pupil is expected to
     * write, and a one-mark recall question given eight lines teaches them the
     * wrong thing about the answer.
     */
    function answerLines(exercise) {
        const marks = exercise.marks || 1;
        const count = Math.max(2, Math.min(10, Math.round(marks * 1.5)));
        let out = '<div class="bk-lines">';
        for (let i = 0; i < count; i++) out += '<div class="bk-line"></div>';
        return out + '</div>';
    }

    const PART_LETTERS = 'abcdefghijklmnop';

    function renderExercise(exercise, index) {
        // Stored questions predate the validator's tidying, so it is applied
        // here too: no "(a)" the renderer adds again, no parts copied into
        // the stem. Index-safe — it never drops a part.
        if (Schema && Schema.tidyExercise) exercise = Schema.tidyExercise(exercise);
        // Divs, not paragraphs: a question can hold a table, and a table
        // inside a <p> makes the browser close the paragraph early, spilling
        // the rest of the question out of its layout.
        const parts = (exercise.parts || []).map((p, i) =>
            `<div class="bk-part">`
            + `<div class="bk-part-q"><span class="bk-part-letter">${PART_LETTERS[i] || '?'})</span>`
            + `${questionText(p.question)} ${marksTag(p.marks)}</div>`
            + answerLines(p)
            + `</div>`
        ).join('');

        return `<div class="bk-ex">`
            + `<div class="bk-ex-q"><span class="bk-ex-n">${index}</span>`
            + `${questionText(exercise.question)} ${exercise.parts ? '' : marksTag(exercise.marks)}</div>`
            + (parts || answerLines(exercise))
            + `</div>`;
    }

    function renderWorkbookSection(entry, language) {
        const { section, number, chapter, startsChapter } = entry;
        const chapterHead = startsChapter && chapter
            ? `<h2 class="bk-chapter"><span>Chapter ${entry.chapterNo}</span>${esc(chapter)}</h2>` : '';

        // Grouped by tier so the difficulty ramp is visible on the page, not
        // just implied by the order. validateExercises already sorted them.
        let html = '';
        let lastTier = null;
        let n = 0;
        for (const exercise of section.exercises || []) {
            if (exercise.tier !== lastTier) {
                if (lastTier !== null) html += '</div>';
                const tierKey = { warmup: 'warmup', practice: 'practice', exam_style: 'examStyle' }[exercise.tier];
                html += `<div class="bk-tier bk-tier-${esc(exercise.tier)}">`
                    + `<h4>${esc(TIER_LABELS[exercise.tier] || exercise.tier)}`
                    + `${tierKey ? l1(language, tierKey) : ''}</h4>`;
                lastTier = exercise.tier;
            }
            html += renderExercise(exercise, ++n);
        }
        if (lastTier !== null) html += '</div>';

        return chapterHead
            + `<section class="bk-section bk-wb-section">`
            + `<h3 class="bk-heading"><span class="bk-num">${esc(number)}</span>${esc(section.heading)}</h3>`
            + html
            + `</section>`;
    }

    /**
     * The answer key, printed at the back rather than beside the question.
     *
     * Keyed by the same section numbers as the questions, so a teacher marking
     * section 3.2 looks up 3.2 — no separate numbering to keep in step.
     */
    function buildAnswerKey(sections, language) {
        const numbered = numberSections(renderable(sections, 'exercises'));
        if (!numbered.length) return '';

        const blocks = numbered.map((entry) => {
            let n = 0;
            const rows = (entry.section.exercises || []).map((exercise) => {
                const index = ++n;
                if (exercise.parts && exercise.parts.length) {
                    const parts = exercise.parts.map((p, i) =>
                        `<li><span class="bk-ak-letter">${PART_LETTERS[i] || '?'})</span>`
                        + `${bodyText(p.answer)} ${marksTag(p.marks)}</li>`
                    ).join('');
                    return `<div class="bk-ak-row"><span class="bk-ak-n">${index}</span>`
                        + `<ol class="bk-ak-parts">${parts}</ol></div>`;
                }
                return `<div class="bk-ak-row"><span class="bk-ak-n">${index}</span>`
                    + `<span>${bodyText(exercise.answer)} ${marksTag(exercise.marks)}</span></div>`;
            }).join('');
            return `<div class="bk-ak-section">`
                + `<h3><span class="bk-num">${esc(entry.number)}</span>${esc(entry.section.heading)}</h3>`
                + rows + `</div>`;
        }).join('');

        return `<section class="bk-answer-key"><h2>Answers${l1(language, 'answers')}</h2>${blocks}</section>`;
    }

    /** The workbook: questions at the front, answers at the back. */
    function buildWorkbookHtml(booklet, sections, opts) {
        opts = opts || {};
        const usable = renderable(sections, 'exercises');
        const numbered = numberSections(usable);
        const language = (HomeLanguages && HomeLanguages.cleanLanguage(booklet && booklet.home_language)) || '';
        return `<main class="bk-doc bk-workbook">`
            + coverPage(booklet, 'workbook', opts.mark)
            + contentsPage(numbered, language)
            + numbered.map((entry) => renderWorkbookSection(entry, language)).join('')
            + (opts.withAnswers === false ? '' : buildAnswerKey(usable, language))
            + (BrandMark ? BrandMark.creditHtml(opts.mark, 'bk') : '')
            + `</main>`;
    }

    /**
     * A complete printable document.
     *
     * `opts.css` inlines the stylesheet (the PDF path, which runs on a page
     * with no base URL, so a <link> could not resolve); otherwise the three
     * stylesheets are linked, fonts first because @font-face must be declared
     * before the rule naming the family.
     */
    function buildPrintPage(booklet, sections, opts) {
        opts = opts || {};
        const kind = opts.kind === 'workbook' ? 'workbook' : 'booklet';
        const body = kind === 'workbook'
            ? buildWorkbookHtml(booklet, sections, opts)
            : buildBookletHtml(booklet, sections, opts);

        const styleTag = opts.css
            ? `<style>${opts.css}</style>`
            : `<link rel="stylesheet" href="/vendor/katex/katex.min.css">`
              + `<link rel="stylesheet" href="${escAttr(opts.fontsHref || '/css/a11y-fonts.css')}">`
              + `<link rel="stylesheet" href="${escAttr(opts.cssHref || '/css/booklet-print.css')}">`;

        // Both are validated against public/js/a11y-profiles.js before they get
        // here and escaped again on the way out: they land in an attribute, so
        // an unescaped query parameter would be an injection.
        // data-kind lets the stylesheet reclaim the callout gutter for the
        // workbook, which has no margin callouts to put in it.
        const attrs = ` data-kind="${kind}"`
                    + (opts.a11y ? ` data-a11y="${escAttr(opts.a11y)}"` : '')
                    + (opts.tint ? ` data-tint="${escAttr(opts.tint)}"` : '');

        const title = (booklet && booklet.title) || 'Revision guide';
        return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc(title)}${kind === 'workbook' ? ' – Workbook' : ''}</title>
${styleTag}
</head><body${attrs}>${body}</body></html>`;
    }

    return {
        esc,
        mathText,
        bodyText,
        numberSections,
        renderBlock,
        // The reader shows the workbook on screen as well as printing it, and
        // one exercise is the unit it renders – the tier grouping around it
        // belongs to whichever view is drawing them.
        renderExercise,
        questionText,
        buildBookletHtml,
        buildWorkbookHtml,
        buildAnswerKey,
        buildPrintPage,
        TIER_LABELS,
    };
}));
